import { useState, useEffect } from "react";
import { loadStripe } from "@stripe/stripe-js";
import type { Stripe } from "@stripe/stripe-js";

export interface UseStripeSetupOptions {
  stripePublishableKey: string;
}

export interface UseStripeSetupReturn {
  stripe: Stripe | null;
  loading: boolean;
  error: string | null;
}

/**
 * Simplified hook for Stripe setup in web components
 * Only loads Stripe for collecting payment method data
 */
export function useStripeSetup(
  options: UseStripeSetupOptions,
): UseStripeSetupReturn {
  const [stripe, setStripe] = useState<Stripe | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!options.stripePublishableKey) {
      setError("Stripe publishable key is required");
      setLoading(false);
      return;
    }

    loadStripe(options.stripePublishableKey)
      .then((stripeInstance) => {
        setStripe(stripeInstance);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message || "Failed to load Stripe");
        setLoading(false);
      });
  }, [options.stripePublishableKey]);

  return {
    stripe,
    loading,
    error,
  };
}
